import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, Equals } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';
import { sampleTier, type ThinLabels } from '../charts/thin';

export type MarketBarInput = {
  /** This job's pay range (min only for a single figure). */
  min: number;
  max?: number;
  p25: number;
  p75: number;
  median: number;
};

export type MarketBarGeometry = {
  /** Percent positions on the bar's own scale. */
  band: { left: number; width: number };
  job: { left: number; width: number };
  median: number;
  /** The job's midpoint is above the market median. */
  above: boolean;
};

const pct = (v: number) => Math.round(v * 1000) / 10;

/**
 * Places the job's range and the market's p25 to p75 band on one scale that covers both with a
 * margin. A single figure gets a small minimum width so it stays visible. Pure, exported for tests.
 */
export function marketBarGeometry({ min, max = min, p25, p75, median }: MarketBarInput): MarketBarGeometry {
  const lo = Math.min(min, p25);
  const hi = Math.max(max, p75);
  const pad = (hi - lo || hi || 1) * 0.1;
  const start = lo - pad;
  const span = hi + pad - start;
  const at = (v: number) => (v - start) / span;
  const jobWidth = Math.max(at(max) - at(min), 0.02);
  return {
    band: { left: pct(at(p25)), width: pct(at(p75) - at(p25)) },
    job: { left: pct(Math.min(at(min), 1 - jobWidth)), width: pct(jobWidth) },
    median: pct(at(median)),
    above: (min + max) / 2 > median,
  };
}

/** Optional parts, for surfaces that show less than a job against the market. */
export type MarketBarOptions = {
  /**
   * A fixed scale shared by every bar in a list (cards across a page), so bars compare. Without it
   * each bar scales to its own values with a margin.
   */
  scale?: { min: number; max: number };
  /** A dashed reference tick (the official median, the typical pay), named in the description. */
  reference?: { value: number };
  /**
   * `inverse` draws in the surrounding text colour, for bars on a brand or dark panel, where the
   * chart colours would not read.
   */
  tone?: 'default' | 'inverse';
  /**
   * How many values the band and median rest on. Under 10 the bar is not drawn (with `thinLabels`
   * the too-few words show instead); 10 to 19 drops the band and marks the median hollow and dashed.
   */
  sampleSize?: number;
  thinLabels?: ThinLabels;
  /** The bar's thickness: `sm` (compact default), `md` (expanded default), `lg` for a bar that is a card's main figure. */
  barSize?: 'sm' | 'md' | 'lg';
};

export type MarketBarProps = Omit<MarketBarInput, 'min' | 'p25' | 'p75' | 'median'> & {
  /** This job's pay; leave out to show only the market (a role or company card). */
  min?: number;
  p25?: number;
  p75?: number;
  median?: number;
  /** One sentence for screen readers: "Market for similar jobs: 3 400 to 4 100 €/mo (median 3 750, 214 jobs)". */
  description: string;
  className?: string;
} & MarketBarOptions &
  (
    | {
        /** 96px, on cards. */ variant?: 'compact';
        p25Label?: undefined;
        p75Label?: undefined;
        sample?: undefined;
        verdictLabels?: undefined;
      }
    | {
        /** Full width in details: labels p25 and p75 and states the sample size, all required. */
        variant: 'expanded';
        p25Label: ReactNode;
        p75Label: ReactNode;
        sample: ReactNode;
        /**
         * Words for where this job sits against the median, shown under the bar with an arrow so the
         * direction never rests on colour alone. Only with a job's pay and a median; leave out to show none.
         */
        verdictLabels?: { above: ReactNode; below: ReactNode; at: ReactNode };
      }
  );

/** A label under a point on the bar, kept inside the bar near either end. */
function tickStyle(pct: number) {
  const shift = pct < 12 ? '0%' : pct > 88 ? '-100%' : '-50%';
  return { left: `${pct}%`, transform: `translateX(${shift})` };
}

const TONE = {
  default: {
    track: 'bg-jc-elevated',
    band: 'bg-jc-border-control',
    above: 'bg-jc-live',
    below: 'bg-jc-ink-secondary',
    median: 'bg-jc-ink',
    rough: 'border-jc-ink bg-jc-surface',
    reference: 'border-jc-ink-secondary',
    label: 'text-jc-ink-secondary',
  },
  inverse: {
    track: 'bg-current/15',
    band: 'bg-current/35',
    above: 'bg-current',
    below: 'bg-current',
    median: 'bg-current',
    rough: 'border-current bg-transparent',
    reference: 'border-current',
    label: 'text-current opacity-75',
  },
} as const;

/**
 * A job's pay against the p25 to p75 band of similar jobs. Callers show it only for verified monthly
 * gross pay with a benchmark, and hide it otherwise. Green only when the job sits above the median.
 * The same bar serves cards that show only the market (leave out `min`), lists that share one
 * `scale`, a `reference` such as the official median, and brand panels (`tone="inverse"`). Thin
 * data follows the chart set's tiers when `sampleSize` is given.
 */
export function MarketBar({
  description,
  variant = 'compact',
  p25Label,
  p75Label,
  sample,
  verdictLabels,
  className,
  scale,
  reference,
  tone = 'default',
  sampleSize,
  thinLabels,
  barSize,
  min,
  max,
  p25,
  p75,
  median,
}: MarketBarProps) {
  const tier = sampleSize === undefined ? 'full' : sampleTier(sampleSize);
  const expanded = variant === 'expanded';
  if (tier === 'none')
    return thinLabels ? <span className={cx('text-xs', TONE[tone].label, className)}>{thinLabels.tooFew}</span> : null;
  const showBand = tier === 'full' && p25 !== undefined && p75 !== undefined;
  const values = [min, max, showBand ? p25 : undefined, showBand ? p75 : undefined, median, reference?.value].filter(
    (v): v is number => v !== undefined,
  );
  let start: number;
  let span: number;
  if (scale) {
    start = scale.min;
    span = Math.max(1, scale.max - scale.min);
  } else {
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    const pad = (hi - lo || hi || 1) * 0.1;
    start = lo - pad;
    span = hi + pad - start;
  }
  const at = (v: number) => pct(Math.min(1, Math.max(0, (v - start) / span)));
  const job =
    min !== undefined
      ? (() => {
          const hi = max ?? min;
          const width = Math.max(at(hi) - at(min), 2);
          return { left: Math.min(at(min), 100 - width), width };
        })()
      : null;
  const above = min !== undefined && median !== undefined && (min + (max ?? min)) / 2 > median;
  const t = TONE[tone];
  const mid = min !== undefined ? (min + (max ?? min)) / 2 : undefined;
  const verdict =
    mid === undefined || median === undefined || !verdictLabels
      ? null
      : Math.abs(mid - median) <= Math.abs(median) * 0.01
        ? { label: verdictLabels.at, glyph: Equals }
        : mid > median
          ? { label: verdictLabels.above, glyph: ArrowUp }
          : { label: verdictLabels.below, glyph: ArrowDown };
  const height = { sm: 'h-2', md: 'h-3', lg: 'h-4' }[barSize ?? (expanded ? 'md' : 'sm')];
  return (
    <div className={cx('flex flex-col gap-1.5', expanded ? 'w-full' : 'w-24', className)}>
      <div
        role="img"
        aria-label={description}
        title={description}
        data-above={above || undefined}
        className={cx('relative rounded-jc-info', t.track, height)}
      >
        <span className="absolute inset-0 overflow-hidden rounded-jc-info">
          {showBand ? (
            <span
              className={cx('absolute inset-y-0', t.band)}
              style={{ left: `${at(p25)}%`, width: `${at(p75) - at(p25)}%` }}
            />
          ) : null}
          {job ? (
            <span
              className={cx('absolute inset-y-0 rounded-jc-info', above ? t.above : t.below)}
              style={{ left: `${job.left}%`, width: `${job.width}%` }}
            />
          ) : null}
        </span>
        {reference ? (
          <span
            className={cx('absolute -inset-y-1 border-l-2 border-dashed', t.reference)}
            style={{ left: `${at(reference.value)}%` }}
          />
        ) : null}
        {median !== undefined ? (
          tier === 'rough' ? (
            <span
              className={cx('absolute top-1/2 size-3 -translate-1/2 rounded-full border-2 border-dashed', t.rough)}
              style={{ left: `${at(median)}%` }}
            />
          ) : (
            <span
              className={cx('absolute -inset-y-1 w-0.5 -translate-x-1/2 rounded-full', t.median)}
              style={{ left: `${at(median)}%` }}
            />
          )
        ) : null}
      </div>
      {expanded ? (
        <>
          {showBand ? (
            <div aria-hidden="true" className={cx('relative h-4 text-xs tabular-nums', t.label)}>
              <span className="absolute whitespace-nowrap" style={tickStyle(at(p25))}>
                {p25Label}
              </span>
              <span className="absolute whitespace-nowrap" style={tickStyle(at(p75))}>
                {p75Label}
              </span>
            </div>
          ) : null}
          {verdict ? (
            <span
              className={cx(
                'flex items-center gap-1 text-sm font-semibold',
                above ? 'text-jc-live' : 'text-jc-ink-secondary',
              )}
            >
              <Icon icon={verdict.glyph} size="sm" className="shrink-0" />
              {verdict.label}
            </span>
          ) : null}
          {sample ? (
            <span className={cx('text-xs', t.label)}>
              {sample}
              {tier === 'rough' && thinLabels ? <>, {thinLabels.rough}</> : null}
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
